import {
  doc,
  getDoc,
  onSnapshot,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  SurveyAnswerValue,
  SurveyParticipantSnapshot,
  SurveyResponse,
} from '@citizen-walk/shared-types';
import { enqueueAction } from '../../lib/actionOutbox';
import { db } from '../../lib/firebase';

/**
 * 自分の survey response を subscribe + submit する hook (Sprint T、user 第 16 弾)。
 *
 * - Path: `/districts/{districtId}/surveys/{surveyId}/responses/{uid}` (1 人 1 回・編集可)
 * - firestore.rules で `respId == auth.uid` + `participantUid == auth.uid` 必須
 * - 新規 submit: `createdAt` を serverTimestamp、`updatedAt` も serverTimestamp
 * - 更新 submit: `createdAt` は既存値を維持、`updatedAt` のみ serverTimestamp に差替
 *
 * **Phase 2 層1 (docs/03 §7.5-3、2026-06-07)**: 書込を SDK 直接 setDoc から
 * **リース非依存・冪等な action outbox 経由** (`lib/actionOutbox.ts`、kind='survey') に変更。
 *   - submit は server ack を待たず即 enqueue するので **送信ハング (机内モード等の ack 待ち) が無い**。
 *   - doc ID = uid 固定で再送は同一 doc 上書き = 冪等 (clientId = 送信先 doc path で coalesce)。
 *   - 実送信は `useActionOutbox` (AppHome/MapPage mount) が online 時に flush。
 *   - `createdAt` の温存は flush 時に getDoc して既存値を使う (createOnlyTimestampFields)。新規/更新の
 *     判定を enqueue 時の in-memory state に依存しないため、並行 submit でも createdAt が巻き戻らない。
 *
 * 旧 v1 `apps/user/src/lib/surveys.ts` の `submitSurveyResponse` 相当 (docs/97 §3 L3)。
 *
 * **R-70 P3b (2026-06-07)**: `selfUids` (現 UID + 端末引き継ぎ元 UID 群) を渡すと、現 UID が未回答でも
 * 引き継ぎ元 UID の回答を pre-fill 用に返す。一覧の「✓回答済」(useMyResponseIds) と、開いたフォームの
 * pre-fill の一貫性を保ち、引き継ぎ後に空フォームから再回答してしまう二重回答を防ぐ。
 * submit は常に現 UID へ native に書く (docs/98 R-70 方針(a)、残重複は分析側 dedup)。
 */

export type SurveyResponseState =
  | { status: 'loading'; response: SurveyResponse | null; submitting: false; submitError: null }
  | { status: 'ready'; response: SurveyResponse | null; submitting: false; submitError: null }
  | { status: 'submitting'; response: SurveyResponse | null; submitting: true; submitError: null }
  | { status: 'error'; response: null; submitting: false; submitError: Error };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

/** Firestore doc → SurveyResponse (defensive narrow、不正値は default fallback)。 */
export function mapSurveyResponse(
  snap: { id: string; data: () => DocumentData },
  surveyId: string,
  districtId: string,
): SurveyResponse {
  const data = snap.data();
  const answers =
    data.answers && typeof data.answers === 'object'
      ? (data.answers as Record<string, SurveyAnswerValue>)
      : {};
  const snapshotRaw = data.participantSnapshot;
  const participantSnapshot: SurveyParticipantSnapshot =
    snapshotRaw && typeof snapshotRaw === 'object'
      ? (snapshotRaw as SurveyParticipantSnapshot)
      : {
          nickname: '',
          ageGroup: null,
          gender: null,
          relationship: null,
          attribute: null,
          joinedSessions: [],
        };
  return {
    surveyId,
    districtId,
    participantUid: typeof data.participantUid === 'string' ? data.participantUid : snap.id,
    participantSnapshot,
    answers,
    schemaVersion: 1,
    surveyVersion: typeof data.surveyVersion === 'number' ? data.surveyVersion : 0,
    createdAt: tsToIso(data.createdAt),
    updatedAt: tsToIso(data.updatedAt),
  };
}

/**
 * response doc の **タイムスタンプ以外**の確定フィールド (純粋関数、outbox payload.body 用)。
 * outbox は `serverTimestamp()` sentinel を保存できないため、body と timestamp 方針を分離する。
 */
export function buildResponseBody(args: {
  uid: string;
  surveyId: string;
  districtId: string;
  surveyVersion: number;
  answers: Record<string, SurveyAnswerValue>;
  participantSnapshot: SurveyParticipantSnapshot;
}): Record<string, unknown> {
  return {
    surveyId: args.surveyId,
    districtId: args.districtId,
    participantUid: args.uid,
    participantSnapshot: args.participantSnapshot,
    answers: args.answers,
    schemaVersion: 1,
    surveyVersion: args.surveyVersion,
  };
}

/**
 * `setDoc` payload 構築 (純粋関数、test しやすいよう module-level に export)。
 * 新規時 `createdAt = serverTimestamp()`、更新時は既存値を維持。`updatedAt` は常に `serverTimestamp()`。
 * outbox 化後は flush 側 (useActionOutbox の timestamp 方針適用) が同等の payload を組むため、本関数は
 * 単体テスト + 直接 setDoc 経路の参照として残す (body 部は `buildResponseBody` に集約)。
 */
export function buildResponsePayload(args: {
  uid: string;
  surveyId: string;
  districtId: string;
  surveyVersion: number;
  answers: Record<string, SurveyAnswerValue>;
  participantSnapshot: SurveyParticipantSnapshot;
  existing: SurveyResponse | null;
  serverTimestampSentinel: unknown;
}): Record<string, unknown> {
  return {
    ...buildResponseBody(args),
    // 新規時のみ serverTimestamp、既存があれば createdAt は維持 (ISO 文字列を温存)。
    createdAt: args.existing ? args.existing.createdAt : args.serverTimestampSentinel,
    updatedAt: args.serverTimestampSentinel,
  };
}

const LOADING_STATE: SurveyResponseState = {
  status: 'loading',
  response: null,
  submitting: false,
  submitError: null,
};

export interface UseSurveyResponseResult {
  state: SurveyResponseState;
  /** 回答を送信する。新規 / 更新は内部で existing の有無で自動判別。 */
  submitResponse: (
    answers: Record<string, SurveyAnswerValue>,
    participantSnapshot: SurveyParticipantSnapshot,
    surveyVersion: number,
  ) => Promise<void>;
}

export function useSurveyResponse(
  districtId: string,
  surveyId: string | null,
  uid: string | null,
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群。省略時は uid 単独 (後方互換)。 */
  selfUids?: string[],
): UseSurveyResponseResult {
  const [keyedState, setKeyedState] = useState<{ key: string; value: SurveyResponseState }>({
    key: '',
    value: LOADING_STATE,
  });
  // state は「どの survey / uid の読込結果か」と組で持つ。survey を切り替えた直後の描画で前の
  // survey の回答を返さないよう、組が今の survey / uid と違えば loading として返す
  // (画面は回答の読込完了を待って form を出すため、前の結果を見ると空の form が出る)。
  const stateKey = surveyId && uid ? `${surveyId}|${uid}` : '';
  const state = keyedState.key === stateKey ? keyedState.value : LOADING_STATE;
  const setState = useCallback(
    (next: SurveyResponseState | ((prev: SurveyResponseState) => SurveyResponseState)) => {
      setKeyedState((prev) => {
        const prevValue = prev.key === stateKey ? prev.value : LOADING_STATE;
        return {
          key: stateKey,
          value: typeof next === 'function' ? next(prevValue) : next,
        };
      });
    },
    [stateKey],
  );
  // 引き継ぎ元 UID (現 UID を除く)。別端末作成のため pre-fill 用に one-shot read。
  const linkedUids = (selfUids ?? []).filter((u) => u && u !== uid);
  const linkedKey = linkedUids.join('|');
  // onSnapshot 発火ごとに採番し、引き継ぎ元 one-shot の async が後続発火に追い越された場合に
  // stale 結果で上書きしないためのガード。
  const runRef = useRef(0);

  // subscribe 自分の response (doc ID = uid)
  useEffect(() => {
    if (!surveyId || !uid) {
      setState({ status: 'loading', response: null, submitting: false, submitError: null });
      return;
    }
    let cancelled = false;
    const ref = doc(db, 'districts', districtId, 'surveys', surveyId, 'responses', uid);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const myRun = ++runRef.current;
        if (snap.exists()) {
          if (!cancelled) {
            setState({
              status: 'ready',
              response: mapSurveyResponse(snap, surveyId, districtId),
              submitting: false,
              submitError: null,
            });
          }
          return;
        }
        // 現 UID 未回答 → 引き継ぎ元 UID を one-shot 確認し pre-fill に使う (二重回答防止)。
        if (linkedUids.length === 0) {
          if (!cancelled) {
            setState({ status: 'ready', response: null, submitting: false, submitError: null });
          }
          return;
        }
        void (async () => {
          let found: SurveyResponse | null = null;
          for (const lu of linkedUids) {
            try {
              const ls = await getDoc(
                doc(db, 'districts', districtId, 'surveys', surveyId, 'responses', lu),
              );
              if (ls.exists()) {
                found = mapSurveyResponse(ls, surveyId, districtId);
                break;
              }
            } catch {
              // claim 上限超で read 拒否されても無視 (その UID 分は未検出に縮退)。
            }
          }
          // 後続の onSnapshot 発火に追い越されていたら破棄 (現 UID の回答が優先)。
          if (!cancelled && myRun === runRef.current) {
            setState({ status: 'ready', response: found, submitting: false, submitError: null });
          }
        })();
      },
      (error) => {
        if (!cancelled) {
          setState({ status: 'error', response: null, submitting: false, submitError: error });
        }
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
    // linkedUids は配列参照が毎回変わるため、安定な linkedKey (join) を dependency にする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, surveyId, uid, linkedKey, setState]);

  const submitResponse = useCallback(
    async (
      answers: Record<string, SurveyAnswerValue>,
      participantSnapshot: SurveyParticipantSnapshot,
      surveyVersion: number,
    ): Promise<void> => {
      if (!surveyId || !uid) {
        throw new Error('surveyId / uid が未確定です');
      }
      setState((prev) => ({
        status: 'submitting',
        response: prev.status === 'submitting' || prev.status === 'ready' ? prev.response : null,
        submitting: true,
        submitError: null,
      }));
      try {
        // リース非依存・冪等な outbox へ enqueue (clientId = 送信先 doc path で coalesce)。
        // createdAt 温存は flush 時に getDoc で行うため、ここで existing を読む必要はない。
        await enqueueAction({
          clientId: `districts/${districtId}/surveys/${surveyId}/responses/${uid}`,
          kind: 'survey',
          op: 'set',
          districtId,
          payload: {
            body: buildResponseBody({
              uid,
              surveyId,
              districtId,
              surveyVersion,
              answers,
              participantSnapshot,
            }),
            serverTimestampFields: ['updatedAt'],
            createOnlyTimestampFields: ['createdAt'],
          },
        });
        // 送信は useActionOutbox の flush に委ねる。onSnapshot が server 確定値で更新するが、
        // submit 直後の UI 即時反映用に一度 ready を確定。
        setState((prev) => ({
          status: 'ready',
          response: prev.response,
          submitting: false,
          submitError: null,
        }));
      } catch (e) {
        setState({
          status: 'error',
          response: null,
          submitting: false,
          submitError: e as Error,
        });
        throw e;
      }
    },
    [districtId, surveyId, uid, setState],
  );

  return { state, submitResponse };
}
